import type { Lens } from "@hookform/lenses";
import type { SizeQuantity } from "../../model/transferLabTypes";
import { QuantityInput } from "./QuantityInput";
import { PricingDisplay } from "./PricingDisplay";
import { usePresetSizePricing } from "../../hooks/usePresetSizePricing";

type PresetSizeCardProps = {
  presetId: string;
  size: SizeQuantity;
  quantityLens: Lens<number>;
  onChange?: (quantity: number) => void;
  artworkId?: string;
  transferTypeId?: string;
  enhancementTier?: string;
};

export function PresetSizeCard({
  presetId,
  size,
  quantityLens,
  onChange,
  artworkId,
  transferTypeId,
  enhancementTier = "full_enhancements"
}: PresetSizeCardProps) {
  // Fetch pricing for this preset size
  const { pricing, isLoading } = usePresetSizePricing({
    artworkId: artworkId || "",
    transferTypeId: transferTypeId || "",
    sizeId: size.id,
    quantityLens,
    width: size.width,
    height: size.height,
    enhancementTier,
    enabled: !!(artworkId && transferTypeId)
  });

  return (
    <div className="flex flex-shrink-0 flex-col" data-testid={`size-card-${size.id}`}>
      {/* Size range label */}
      <div className="mb-1 text-center text-sm font-semibold text-gray-900">
        {size.displayValue}
      </div>

      {/* Dimensions */}
      <div className="mb-3 text-center text-xs text-gray-500">
        {Number(size.width).toFixed(2)}&quot; x {Number(size.height).toFixed(2)}&quot;
      </div>

      {/* Quantity controls */}
      <QuantityInput
        id={`preset-quantity-${presetId}-${size.id}`}
        lens={quantityLens}
        {...(onChange && { onChange })}
      />

      {/* Pricing display below quantity */}
      <div className="mt-1 text-center text-xs">
        <PricingDisplay pricing={pricing} isLoading={isLoading} quantity={size.quantity} />
      </div>
    </div>
  );
}
